import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Breadcrumb, Button, Input, Modal, PageHeader, SelectMenu, SettingsPanel, SettingsRow,
  Spinner, WeeklyHours, type WeeklyHoursValue,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { openSlotSidebar } from './openSlotSidebar';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { TIMEZONES, rulesToWeekly, weeklyToRules, type ApiRule } from '../../openslot/helpers';

interface ScheduleSummary { id: string; name: string; isDefault: boolean }
interface ScheduleDetail { id: string; name: string; timezone: string; isDefault: boolean; rules: ApiRule[] }

export function AvailabilityPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [schedules, setSchedules] = useState<ScheduleSummary[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [timezone, setTimezone] = useState('America/New_York');
  const [hours, setHours] = useState<WeeklyHoursValue | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadSchedule = useCallback(async (id: string) => {
    setHours(null);
    const d = await apiGet<ScheduleDetail>(`/api/openslot/schedules/${id}`);
    setSelectedId(d.id);
    setTimezone(d.timezone);
    setHours(rulesToWeekly(d.rules));
  }, []);

  const loadSchedules = useCallback(
    async (selectId?: string) => {
      const list = await apiGet<ScheduleSummary[]>('/api/openslot/schedules');
      setSchedules(list);
      const target = selectId ?? selectedId ?? list.find((s) => s.isDefault)?.id ?? list[0]?.id;
      if (target) await loadSchedule(target);
    },
    [loadSchedule, selectedId],
  );

  useEffect(() => {
    loadSchedules().catch(() => setErr('Failed to load schedules.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createSchedule = async () => {
    if (!newName.trim()) return;
    setErr(null);
    try {
      const { id } = await apiPost<{ id: string }>('/api/openslot/schedules', { name: newName.trim() });
      setCreating(false);
      setNewName('');
      await loadSchedules(id);
    } catch {
      setErr('Could not create schedule.');
    }
  };

  const save = async () => {
    if (!hours || !selectedId) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await apiPut(`/api/openslot/schedules/${selectedId}`, { timezone, rules: weeklyToRules(hours) });
      setMsg('Schedule saved.');
      // refresh the names (in case default flag changed)
      setSchedules(await apiGet<ScheduleSummary[]>('/api/openslot/schedules'));
    } catch {
      setErr('Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  const options = (schedules ?? []).map((s) => ({
    value: s.id,
    label: `${s.name}${s.isDefault ? ' (default)' : ''}`,
  }));

  return (
    <AppChrome secondarySidebar={openSlotSidebar(navigate, pathname)}>
      <PageHeader
        title={<>Open Slot - <span className="fw-normal">Availability</span></>}
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Open Slot' }, { label: 'Availability' }]} />}
      />
      <div className="content">
        {msg && <Alert variant="success" icon="ph-check-circle" dismissible>{msg}</Alert>}
        {err && <Alert variant="danger" icon="ph-warning" dismissible>{err}</Alert>}
        {!schedules && <Spinner />}

        {schedules && (
          <SettingsPanel title="Working hours" description="Named weekly schedules you can attach to scheduling events.">
            <SettingsRow title="Schedule" description="Pick a schedule to edit, or create a new one.">
              <div style={{ maxWidth: 360 }}>
                <SelectMenu
                  icon="ph-calendar"
                  value={selectedId ?? ''}
                  onChange={(id) => loadSchedule(id)}
                  options={options}
                  action={{ label: 'Create schedule', icon: 'ph-plus', onClick: () => setCreating(true) }}
                />
              </div>
            </SettingsRow>

            <SettingsRow title="Timezone" description="Hours below are interpreted in this timezone.">
              <div style={{ maxWidth: 360 }}>
                <SelectMenu icon="ph-globe" value={timezone} onChange={setTimezone} options={TIMEZONES.map((t) => ({ value: t, label: t }))} />
              </div>
            </SettingsRow>

            <SettingsRow title="Weekly hours" description="Toggle a day on and set one or more time ranges.">
              {!hours ? <Spinner size="sm" /> : (
                <>
                  <WeeklyHours value={hours} onChange={setHours} />
                  <div className="mt-3">
                    <Button variant="primary" disabled={busy} onClick={save}>Save schedule</Button>
                  </div>
                </>
              )}
            </SettingsRow>
          </SettingsPanel>
        )}

        <Modal
          open={creating}
          onClose={() => { setCreating(false); setNewName(''); }}
          title="Create schedule"
          footer={
            <>
              <Button variant="light" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</Button>
              <Button variant="primary" disabled={!newName.trim()} onClick={createSchedule}>Create</Button>
            </>
          }
        >
          <Input
            label="Schedule name"
            placeholder="Working Hours, Exclusive Hours, etc."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) void createSchedule();
            }}
          />
        </Modal>
      </div>
    </AppChrome>
  );
}
